import React from 'react'
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Spinner,
  Chip
} from '@heroui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useBalance } from '@/contexts/BalanceContext'
import { configApi } from '@/services/api'
import { ThemeToggle } from './ThemeToggle'
import { LanguageSwitcher } from './LanguageSwitcher'
import { CountryBlockingHandler } from './CountryBlockingHandler'
import { GamepadIcon, Wallet } from 'lucide-react'
import { useTypedTranslation } from '@/hooks/useTypedTranslation'
import Decimal from 'decimal.js'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, isAuthenticated, logout } = useAuth()
  const { mainCurrencyBalance, mainCurrency, isLoading: balanceLoading } = useBalance()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const { t } = useTypedTranslation(['common', 'auth'])

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['config'],
    queryFn: () => configApi.getConfig(),
    retry: false,
  })

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleHowToPlay = () => {
    navigate('/how-it-works')
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsMenuOpen(false)
  }

  const userBalance = Decimal(mainCurrencyBalance?.balance || 0).mul(100).floor().div(100).toNumber()

  return (
    <>
      <div className="min-h-screen bg-background pb-28">
        <Navbar
          isMenuOpen={isMenuOpen}
          onMenuOpenChange={setIsMenuOpen}
          className="border-b border-divider"
          maxWidth="full"
        >
          <NavbarContent>
            <NavbarMenuToggle
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              className="sm:hidden"
            />
            <NavbarBrand>
              <RouterLink to="/" className="font-bold text-inherit max-sm:text-sm">
                {t('app.name')}
              </RouterLink>
            </NavbarBrand>
          </NavbarContent>

          <NavbarContent className="hidden sm:flex gap-4 rtl:gap-x-reverse" justify="center">
            <NavbarItem>
              <Button
                variant="light"
                className='!px-2'
                onPress={handleHowToPlay}
              >
                <GamepadIcon /> {t('navigation.howToPlay')}
              </Button>
            </NavbarItem>
          </NavbarContent>

          <NavbarContent justify="end" className="gap-3 rtl:gap-x-reverse">
            {/* Language Switcher */}
            <NavbarItem>
              <LanguageSwitcher />
            </NavbarItem>

            {/* Balance Display */}
            {isAuthenticated && (
              <NavbarItem className="hidden sm:flex">
                {balanceLoading || configLoading ? (
                  <div className="flex items-center gap-2 rtl:gap-x-reverse px-3 py-1 bg-default-100 rounded-lg">
                    <Wallet className="w-4 h-4" />
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <Chip
                    startContent={<Wallet className="w-4 h-4" />}
                    variant="flat"
                    color="success"
                    size="lg"
                    className="cursor-pointer"
                    onClick={() => handleNavigate('/deposit')}
                  >
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(userBalance)} {mainCurrency || config?.data?.data?.mainCurrency}
                  </Chip>
                )}
              </NavbarItem>
            )}

            {isAuthenticated ? (
              <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Avatar
                    isBordered
                    as="button"
                    className="transition-transform"
                    color="secondary"
                    name={user?.nickname || user?.email}
                    src={user?.avatar || undefined}
                    showFallback
                    size="sm"
                  />
                </DropdownTrigger>
                <DropdownMenu aria-label="Profile Actions" variant="flat">
                  <DropdownItem key="profile" className="h-14 gap-2">
                    <p className="font-semibold">{t('navigation.signedInAs')}</p>
                    <p className="font-semibold">{user?.nickname || user?.email}</p>
                  </DropdownItem>
                  <DropdownItem key="profile-page" onPress={() => handleNavigate('/me')}>
                    {t('navigation.profile')}
                  </DropdownItem>
                  <DropdownItem key="deposit" onPress={() => handleNavigate('/deposit')}>
                    {t('navigation.deposit')}
                  </DropdownItem>
                  <DropdownItem key="withdraw" onPress={() => handleNavigate('/withdraw')}>
                    {t('navigation.withdraw')}
                  </DropdownItem>
                  {user?.role === 'ADMIN' ? (
                    <DropdownItem key="admin" onPress={() => handleNavigate('/admin')}>
                      {t('navigation.admin')}
                    </DropdownItem>
                  ) : null}
                  <DropdownItem key="logout" color="danger" onPress={handleLogout}>
                    {t('navigation.logout')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            ) : (
              <div className="flex gap-2 rtl:gap-x-reverse">
                <Button
                  as={RouterLink}
                  to="/login"
                  variant="ghost"
                  size="sm"
                >
                  {t('navigation.login')}
                </Button>
                <Button
                  as={RouterLink}
                  to="/register"
                  color="primary"
                  size="sm"
                  className='max-sm:hidden'
                >
                  {t('navigation.signup')}
                </Button>
              </div>
            )}
          </NavbarContent>

          <NavbarMenu>
            {isAuthenticated ? (
              <>
                {/* Balance in mobile menu */}
                <NavbarMenuItem>
                  <div className="flex items-center gap-3 rtl:gap-x-reverse p-3 bg-default-100 rounded-lg w-full">
                    <Wallet className="w-5 h-5 text-success" />
                    <div>
                      <p className="text-sm text-default-600">{t('navigation.balance')}</p>
                      {balanceLoading || configLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <p className="font-semibold text-success">
                          {Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(userBalance)} {mainCurrency || config?.data?.data?.mainCurrency}
                        </p>
                      )}
                    </div>
                  </div>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => {
                      handleHowToPlay()
                      setIsMenuOpen(false)
                    }}
                  >
                    <GamepadIcon /> {t('navigation.howToPlay')}
                  </Button>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => handleNavigate('/deposit')}
                  >
                    {t('navigation.deposit')}
                  </Button>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => handleNavigate('/withdraw')}
                  >
                    {t('navigation.withdraw')}
                  </Button>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => handleNavigate('/me')}
                  >
                    {t('navigation.profile')}
                  </Button>
                </NavbarMenuItem>
                {user?.role === 'ADMIN' && (
                  <NavbarMenuItem>
                    <Button
                      variant="light"
                      className="w-full justify-start rtl:justify-end"
                      onPress={() => handleNavigate('/admin')}
                    >
                      {t('navigation.admin')}
                    </Button>
                  </NavbarMenuItem>
                )}
                <NavbarMenuItem>
                  <Button
                    color="danger"
                    variant="flat"
                    onPress={handleLogout}
                    className="w-full justify-start rtl:justify-end"
                  >
                    {t('navigation.logout')}
                  </Button>
                </NavbarMenuItem>
              </>
            ) : (
              <>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => {
                      handleHowToPlay()
                      setIsMenuOpen(false)
                    }}
                  >
                    <GamepadIcon /> {t('navigation.howToPlay')}
                  </Button>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    variant="light"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => handleNavigate('/login')}
                  >
                    {t('navigation.login')}
                  </Button>
                </NavbarMenuItem>
                <NavbarMenuItem>
                  <Button
                    color="primary"
                    size="sm"
                    variant="solid"
                    className="w-full justify-start rtl:justify-end"
                    onPress={() => handleNavigate('/register')}
                  >
                    {t('navigation.signup')}
                  </Button>
                </NavbarMenuItem>
              </>
            )}
          </NavbarMenu>
        </Navbar>

        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {children}
        </main>

        <ThemeToggle />

        {/* Country Blocking Handler */}
      </div>
      <CountryBlockingHandler />
    </>
  )
} 